// The **device-local trace store** (`rounds-local-trace-store`; design **D-C**/**D-D**, tasks
// 5.1–5.6).
//
// A practice run's season is written to origin-private storage on the player's own device and read
// back from there, so scrubbing costs no request and the bytes never leave the phone.
//
// ## What lives here, and what lives in Rust
//
// The store is split along the line the medium itself draws:
//
//   * **The writer's commit protocol is `waggle-wasm`'s** (`waggle-wasm/src/opfs.rs`): components
//     appended, durably flushed, manifest written last, generation published atomically,
//     reclamation on failure. It implements the *existing* `waggle_round::trace::TraceSink`, which
//     is why the trace format does not change and why nothing in the shipped decoder needed editing.
//   * **This file is the medium and the reader.** OPFS **sync** access handles are worker-only,
//     which is where the sim runs anyway, and async handles work on the main thread, which is where
//     the review surface is. So the writing half of this file is the `OpfsHost` the Rust sink drives,
//     and the reading half is a `TraceReader`-shaped surface over the published bytes.
//
// ## The commit marker is enforced here, not inherited
//
// `rounds-local-trace-store` is explicit that the transactional rule "is not inherited for free
// merely because the writer and the reader are now the same process: a reader that resolved against
// an unpublished local directory would bypass the commit marker, the per-component checksums and the
// index bounds validation, which is exactly the plausible-but-false-replay class **D-121** guards."
//
// So {@link LocalTraceStore.open} resolves **only** against a published trace directory. An
// unpublished generation is a different directory (`.generations/<runId>/`), is never enumerated by
// the read path, and is reclaimed on the next store initialisation — which is what "startup
// reconciliation" means for a runtime with no persistent process across a restart.
//
// ## The one in-process exception is a second entry point, not a relaxation of the first
//
// The same requirement carves out one narrow permission: "within that constraint the review surface
// MAY read the portion already written **while the run is still stepping**". That is what
// {@link LocalTraceStore.openInFlight} serves, and it is deliberately a *different method with a
// different result type* — see its own doc comment. `open` is unchanged, answers `absent` for a
// staged generation exactly as before, and is the only thing the narrowed
// {@link LocalTraceStore.asPracticeTraceStore} seam exposes; nothing that believes it read a
// published, checksum-verified trace can be holding an unverified prefix instead.
//
// ## Three outcomes, deliberately distinct
//
// A consumer must be able to tell "this run was never made" from "this run's replay was cleared"
// from "this run's data is damaged" ({@link LocalTraceOpen}):
//
//   | Outcome | What it means | What survives |
//   |---|---|---|
//   | `absent` | No record and no published manifest. Never made, or never committed. | nothing |
//   | `evicted` | The record survives; the bytes were reclaimed to make room. | ordinal, configuration descriptor, score |
//   | `corrupt` | The bytes are there and do not match what the manifest declares. | the typed component and message |
//
// "Losing the bytes costs scrubbing, never the record of the run", so eviction removes the whole
// published directory — detail, index and the manifest that declared them, together — and leaves the
// run's row in {@link LocalTraceStore.listRuns} marked unscrubbable. Removing the tiers but keeping
// the manifest would leave "a manifest claiming components that are gone", which the requirement
// forbids by name.
//
// ## The atomic publication costs a copy, because OPFS has no rename
//
// `FsTraceSink` publishes with a single `rename`, which is atomic and free. The File System Access
// API has no standard move or rename between directories, so publication here writes the
// generation's already-flushed bytes into the published directory and then writes the **manifest
// last**. That preserves every observable clause — an interruption part-way leaves a directory with
// no commit marker, which every reader treats as absent — but it has a cost worth stating rather than
// discovering: a run's **peak** storage is about twice its published size (~458 MB for the measured
// 229 MB run, or 4.4% of the phone's measured 10.2 GB origin quota). Within budget, but it is the
// reason the run driver's quota path can fail at publication and not only while stepping.
//
// The alternative — writing components straight into `<runId>/` and letting the manifest's absence
// mark it unpublished — would remove the copy, but `rounds-trace` requires "a per-run **generation
// directory**" whose leftovers are reclaimed, and collapsing the two directories would put
// uncommitted bytes in the namespace the read path enumerates. Flagged as a deliberate trade-off, not
// settled silently.
//
// ## Exactly two read shapes
//
// {@link LOCAL_READ_SHAPES} is the whole surface — coarse-whole and detail-by-inclusive-tick-range,
// the two ROUNDS-MODE §5.4 defines — and it is a runtime value precisely so "no third read shape
// exists" is a test a machine can run rather than a claim a reviewer has to check by reading. An
// inverted or over-wide range is a **typed refusal, never a clamp**; a tick the detail tier does not
// contain is **absent**, a normal result. Those two are different on purpose.

import {
  type CoarseSample,
  type DecodedTrace,
  type DetailRecord,
  type InFlightPrefix,
  type TraceComponentName,
  type TraceManifest,
  TraceCorruptionError,
  TraceUnsupportedVersionError,
  decodeInFlightPrefix,
  openTrace,
  parseTraceManifest,
} from '../../net/rounds-trace';
import { reviewTraceFromDecoded } from '../../net/rounds-review-source';
import type { TraceLoad } from '../../net/rounds-review-source';
import type { PracticeRunRecord, PracticeTraceStore } from './practice-runner';
import { HIVE_PER_CELL_CAPACITY, SIM_TICK_RATE_HZ } from '../../generated/sim-constants';
import {
  probeRunLiveness,
  withRunLeaseIfFree,
  type LockManagerLike,
  type RunLiveness,
} from './run-lease';

// ---------------------------------------------------------------------------------------------
// The layout. Both halves of the store address these names; `waggle-wasm`'s `bindings.rs` exports
// them from the Rust side so the two cannot silently disagree — a disagreement would make every
// locally written trace read as absent and look like a commit-marker bug.
// ---------------------------------------------------------------------------------------------

/**
 * The store's directory inside the origin-private root. Mirrors `opfs::LOCAL_TRACE_ROOT`.
 *
 * It is the **default** rather than the only value from `device-benchmark` on
 * ({@link LocalTraceStoreOptions.traceRoot}) — the player's practice traces and a device
 * benchmark's throwaway ones must not share one directory. Nothing about the shipped practice path
 * changed: it passes no override and lands here.
 */
export const LOCAL_TRACE_ROOT = 'practice-traces';
/** Unpublished generations live here. Mirrors `waggle_round::trace::GENERATIONS_DIR`. */
export const GENERATIONS_DIR = '.generations';
/** The commit marker. Mirrors `waggle_round::trace::MANIFEST_FILE`. */
export const MANIFEST_FILE = 'manifest.json';
/** The run records index — the store's own file, not part of the trace format. Kept outside every
 * trace directory on purpose: it is what must survive eviction. */
export const RUN_RECORDS_FILE = 'runs.json';

/** The trace's four components, by the manifest's own key. Mirrors `ComponentKind::file_name`. */
export const COMPONENT_FILES = {
  coarse: 'coarse.bin',
  detail: 'detail.bin',
  index: 'index.bin',
  balance: 'balance.toml',
} as const;

/** A component of a locally written trace, including the `balance` component the client does not
 * decode but must still checksum. */
export type LocalComponentName = keyof typeof COMPONENT_FILES;

// ---------------------------------------------------------------------------------------------
// The medium, narrowed
// ---------------------------------------------------------------------------------------------

/** The synchronous, worker-only handle. Narrowed to what the writer actually calls. */
export interface OpfsSyncAccessHandle {
  write(buffer: ArrayBufferView, options?: { at?: number }): number;
  /** Resizes the file. Needed because a write **grows** a file and never shrinks one — see
   * {@link writeWhole}. */
  truncate(newSize: number): void;
  getSize(): number;
  flush(): void;
  close(): void;
}

/**
 * Replaces a file's whole contents: write at 0, then cut the file to what was written.
 *
 * The truncate is the load-bearing half. Run ids are the *server's* and the medium is the *device's*,
 * so one id can arrive twice over the life of an origin's storage — a server restored from a fresh
 * root, or the dev harness, restarts its counter while OPFS keeps every trace it was ever handed.
 * Without the cut, a shorter component landing over a longer predecessor leaves the old run's tail
 * behind: bytes the new manifest neither declares nor checksums, which read back as "does not match
 * the checksum its manifest declares" and lose the run. Every whole-file write in this store goes
 * through here for that reason; `appendTo` deliberately does not (it writes at a growing extent into
 * a generation directory that `beginRun` has already reclaimed).
 */
function writeWhole(access: OpfsSyncAccessHandle, bytes: Uint8Array): void {
  access.write(bytes, { at: 0 });
  access.truncate(bytes.byteLength);
  access.flush();
}

export interface OpfsFileHandle {
  readonly kind: 'file';
  readonly name: string;
  getFile(): Promise<File>;
  createSyncAccessHandle(): Promise<OpfsSyncAccessHandle>;
}

export interface OpfsDirectoryHandle {
  readonly kind: 'directory';
  readonly name: string;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<OpfsDirectoryHandle>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<OpfsFileHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
  keys(): AsyncIterableIterator<string>;
}

// ---------------------------------------------------------------------------------------------
// The read surface
// ---------------------------------------------------------------------------------------------

/**
 * Exactly the two read shapes ROUNDS-MODE §5.4 defines, and no others.
 *
 * A runtime array rather than a type alone, so `rounds-local-trace-store`'s "the local store's read
 * surface is enumerated / it offers coarse-whole and detail-by-range only" is a machine-checkable
 * property. {@link LocalTraceReader} is asserted to have exactly these own keys.
 */
export const LOCAL_READ_SHAPES = ['coarseWhole', 'detailByRange'] as const;

export type LocalReadShape = (typeof LOCAL_READ_SHAPES)[number];

/**
 * The widest detail range the local reader will serve, in ticks (two minutes at 15 Hz).
 *
 * "A range wider than a reader-defined maximum ... MUST be refused with a typed error rather than
 * clamped silently." A cap exists because a range read is a contiguous byte read: an unbounded range
 * is an unbounded allocation on a phone, and silently narrowing it would hand a caller fewer ticks
 * than it asked for with no way to notice.
 */
export const LOCAL_MAX_DETAIL_TICKS = 1800;

/** Why a range was refused. Typed, so a caller never parses prose. */
export type LocalRangeRefusal = 'inverted_range' | 'range_too_wide';

/** A refused range. Distinct from corruption: the trace is fine, the request was not. */
export class LocalRangeError extends Error {
  constructor(
    readonly reason: LocalRangeRefusal,
    message: string,
  ) {
    super(message);
    this.name = 'LocalRangeError';
  }
}

/** A detail range's answer. `absent` is a **normal result**, not an error — a request for a tick the
 * detail tier does not contain is answered, not refused. */
export type LocalDetailRange =
  | { kind: 'records'; records: readonly DetailRecord[] }
  | { kind: 'absent' };

/**
 * The local store's read surface: the two shapes, and nothing else.
 *
 * Structurally identical in what it *serves* to the HTTP reader's coarse-whole and detail-by-range
 * routes, which is what lets the shipped decoder and review surface consume a local trace unchanged
 * (`rounds-local-trace-store`: "Holding the read surface identical is what lets the shipped decoder
 * and review surface consume a local trace unchanged").
 */
export interface LocalTraceReader {
  /** The coarse tier, whole and in tick order. Downloaded whole on the HTTP path too — here it is
   * simply already local. */
  coarseWhole(): readonly CoarseSample[];
  /**
   * The detail records for the inclusive tick range `[from, to]`.
   *
   * @throws {LocalRangeError} for an inverted range or one wider than
   *   {@link LOCAL_MAX_DETAIL_TICKS}. Never clamped.
   */
  detailByRange(from: number, to: number): LocalDetailRange;
}

// ---------------------------------------------------------------------------------------------
// Opening
// ---------------------------------------------------------------------------------------------

/**
 * The three distinguishable failures plus the success, as one closed union.
 *
 * This is deliberately **wider** than the shipped `TraceLoad` (`loaded | corrupt`), and the widening
 * is a decision rather than an oversight: `rounds-local-trace-store` requires that "a consumer MUST
 * be able to distinguish 'this run was never made', 'this run's replay was cleared' and 'this run's
 * data is damaged'", and `TraceLoad` cannot express either of the first two. Rather than add arms to
 * a union the graded-review screen already switches over exhaustively, the store answers in its own
 * vocabulary and {@link LocalTraceStore.asPracticeTraceStore} narrows to `TraceLoad` at the one seam
 * that wants it.
 */
export type LocalTraceOpen =
  | { kind: 'loaded'; reader: LocalTraceReader; decoded: DecodedTrace; record: PracticeRunRecord }
  /** No record and no published manifest. Never made, or never committed. */
  | { kind: 'absent' }
  /** The record survives; the bytes were reclaimed to make room. Scrubbing is unavailable — not an
   * error, and never presented as one. */
  | { kind: 'evicted'; record: PracticeRunRecord }
  /** The bytes do not match what the manifest declares, or the index does not fit the detail tier.
   * Fails closed: no record from any component is served. */
  | { kind: 'corrupt'; component: TraceComponentName; message: string }
  /**
   * The trace is intact but was written at a format version this build no longer reads — **stale,
   * not damaged** (`bee-state-flash` task 4.5).
   *
   * Its own arm rather than a corruption, because the two are different facts about the player's
   * device and imply different actions: a damaged trace means something went wrong and is worth
   * reporting, while a stale one means only that the app was updated since the run was written.
   * The union is closed, so "stop saying corrupt" is only implementable if the result it becomes
   * exists.
   *
   * **The trace's bytes are left in place.** When a sweep reclaims such a trace, and how it counts
   * against the retention budget until then, belongs to the capability that owns local retention;
   * leaving the bytes alone is the one choice that pre-empts that decision in neither direction.
   */
  | { kind: 'unsupported'; formatVersion: number; supportedVersion: number };

/**
 * What {@link LocalTraceStore.openInFlight} answers: the staged generation's readable prefix, or
 * nothing.
 *
 * Two arms, and neither of them is {@link LocalTraceOpen}'s. That is the point. `open`'s union has a
 * `loaded` arm carrying a checksum-verified {@link DecodedTrace}; this one has an `in-flight` arm
 * carrying an {@link InFlightPrefix} branded `in-process-unverified`. The two are not assignable to
 * one another in either direction, so the unverified value cannot be returned from the published
 * path by a slip, and a consumer written against the published path cannot silently accept this one
 * — the difference is in the types rather than in a comment asking the reader to be careful.
 *
 * There is deliberately no `corrupt` arm. Corruption is a claim about a *declaration* being
 * violated, and a staged generation declares nothing: the bytes past the write head are not damaged,
 * they are not there yet. An unreadable tail therefore narrows the prefix rather than producing a
 * failure — see {@link decodeInFlightPrefix}.
 */
export type InFlightTraceOpen =
  /** The generation exists and this is what it holds so far. `writeHeadTick` is `null` when nothing
   * has been written yet — an extent of zero width, which is a normal state and not an absence. */
  | { kind: 'in-flight'; prefix: InFlightPrefix }
  /** There is no generation for this run: it was never begun, it was discarded, it was published, or
   * a reload reclaimed it. */
  | { kind: 'absent' };

/**
 * The per-component checksum check.
 *
 * An injected seam because the honest implementation is not JavaScript's:
 * `client/src/net/rounds-trace.ts` records a deliberate decision that "the client has no BLAKE3 and
 * deliberately does not pull one in", since on the HTTP path the server's fail-closed reader
 * verifies the digest. A **locally** written trace has no server in its path, so the check crosses
 * the wasm boundary to `waggle_wasm::bindings::verify_component_checksum` — the same `blake3` the
 * writer folded as it handed bytes to the sink, rather than a second implementation in a second
 * language that could disagree with it.
 *
 * Required, with no default: a store that silently skipped verification would serve a damaged trace
 * as a healthy one, which is the failure this whole surface exists to prevent.
 */
export interface LocalTraceVerifier {
  verify(declaredHex: string, bytes: Uint8Array): boolean;
}

/** The one function this store needs from the wasm module. Declared structurally rather than imported,
 * so nothing here has a static dependency on generated build output — `client/src/generated/wasm/` is
 * produced by `cargo xtask build-wasm-client` and gitignored, and a static import of it would break
 * `tsc` and `vite build` on a fresh clone that had not run the provisioning step. */
export interface WasmChecksumModule {
  verify_component_checksum(expectedHex: string, bytes: Uint8Array): boolean;
}

/**
 * The **production** verifier: the same BLAKE3 the writer folded, reached across the `wasm-bindgen`
 * boundary (`waggle_wasm::bindings::verify_component_checksum`).
 *
 * The composition root passes the module it has already loaded to run the sim — the store never loads
 * one of its own, because the load is a bootstrap concern with its own failure surface and doing it
 * twice would put two `WebAssembly.Instance`s in one page for no reason.
 */
export function wasmLocalTraceVerifier(module: WasmChecksumModule): LocalTraceVerifier {
  return {
    verify(declaredHex: string, bytes: Uint8Array): boolean {
      return module.verify_component_checksum(declaredHex, bytes);
    },
  };
}

/** A verifier that accepts nothing whose digest it was not told. For tests that need determinism
 * without the wasm module: it compares against a caller-supplied table. */
export function tableVerifier(digests: ReadonlyMap<string, string>): LocalTraceVerifier {
  return {
    verify(declaredHex: string, bytes: Uint8Array): boolean {
      return digests.get(declaredHex) === fingerprint(bytes);
    },
  };
}

/** A cheap, order-sensitive content fingerprint. **Not** a checksum and never written into a
 * manifest — it exists so {@link tableVerifier} can tell "these bytes" from "different bytes"
 * without a real hash. */
export function fingerprint(bytes: Uint8Array): string {
  let h = 0x811c9dc5;
  for (const b of bytes) {
    h = ((h ^ b) * 0x01000193) >>> 0;
  }
  return `${bytes.byteLength}:${h.toString(16)}`;
}

// ---------------------------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------------------------

/**
 * One run's open generation — the medium half of `waggle-wasm`'s `OpfsHost`.
 *
 * The Rust sink decides the *order* these are called in; this object does the moving of bytes and
 * reports what happened. Nothing here may publish without a manifest, and nothing here exposes a
 * published component: reading is {@link LocalTraceStore.open}'s job, and it resolves only against a
 * commit marker.
 */
export interface LocalGeneration {
  /** Appends to a component inside the unpublished generation.
   * @throws the medium's own error, unchanged — a `QuotaExceededError` must reach the run driver
   *   recognisable, because "evict oldest and retry once" is keyed off it and off nothing else. */
  append(component: LocalComponentName, bytes: Uint8Array): Promise<void>;
  /** Durably flushes every component. The medium's `fsync`. */
  flushComponents(): Promise<void>;
  /** Writes the commit marker inside the still-unpublished generation. */
  writeManifest(bytes: Uint8Array): Promise<void>;
  /**
   * Publishes the generation atomically and records the run.
   *
   * Retention is applied **before** the publication, per "the oldest retained trace is evicted
   * before the new one is published" — so the store is never transiently over its own ceiling.
   *
   * Returns a {@link LocalPublication} rather than nothing, so tidying up the staged copy afterwards
   * can **fail visibly without failing the publication** — the shape
   * `waggle_round::trace::writer::Publication` already established for its post-commit durability
   * step, and for the same reason: the manifest is the commit marker, so everything after it is a
   * warning about housekeeping and never a retraction of the commit.
   */
  publish(record: PublishedRunFacts): Promise<LocalPublication>;
  /** Reclaims the generation. Nothing observable survives. */
  discard(): Promise<void>;
  /**
   * The bytes written **so far** for a component.
   *
   * The one narrow in-process exception (`rounds-trace`, design **D-D**): "a writer MAY expose the
   * extent it has already written to its own in-process reader while the run is still stepping", so
   * the playhead chases the write head. Its shape is the limit — a **count**, not bytes. A caller
   * learns how far it may read and cannot obtain, export or persist a component through it, and a
   * reclaimed generation reports zero.
   */
  writtenExtent(component: LocalComponentName): number;
  /** The number of detail records appended so far, so an in-flight reader can bound its playhead
   * without reading the index. A count, for the same reason as above. */
  readonly inFlightTicks: number;
}

/**
 * What a publication reports **after** its commit marker landed.
 *
 * Mirrors `waggle_round::trace::writer::Publication`: the run is published, and this carries whatever
 * went wrong in the housekeeping that followed. `null` is the ordinary case.
 */
export interface LocalPublication {
  /**
   * Why the staged generation directory could not be removed, or `null`.
   *
   * Surfaced rather than swallowed because a leftover staged directory is **evidence** — it is what an
   * interrupted run leaves behind — and a silent one turns a successful run into a false report that
   * it never finished.
   */
  readonly stagingRemovalWarning: string | null;
}

/** What publishing records about a run. Survives eviction; that is its whole purpose. */
export interface PublishedRunFacts {
  runId: number;
  /** 1-based within this practice colony. Not a rank. */
  number: number;
  score: number;
  /** The configuration descriptor: the seed the run was frozen with — the whole of it now that the
   * rival count is fixed in the engine rather than chosen per run. A row written by an older build
   * carries a `rivals` field beside it, which is simply not read. */
  seed: string;
  /** The document ordinal the run simulated. See {@link PracticeRunRecord.documentVersion} for why
   * this is stored rather than fetched. */
  documentVersion: number;
  completedAt: number;
}

// ---------------------------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------------------------

export interface LocalTraceStoreOptions {
  /** The origin-private root, as `navigator.storage.getDirectory()` resolves it. */
  root: OpfsDirectoryHandle;
  verifier: LocalTraceVerifier;
  /** Keep-last-N completed traces. `rounds-local-trace-store`: "at most a configured number of
   * completed traces per practice sandbox, evicting oldest-first". */
  retain?: number;
  /**
   * The store's directory name inside the origin-private root. Defaults to
   * {@link LOCAL_TRACE_ROOT} — the shipped practice path passes nothing and is unchanged.
   *
   * It is a parameter because `device-benchmark` (design **D-B**) needs a store that writes **real
   * bytes through the real publication path** — publication is a cost its measurement must include
   * — while touching nothing the player owns. Sharing this root would have been actively harmful,
   * not merely untidy: {@link LocalGeneration.publish} evicts the oldest resident run *before*
   * publishing, so a benchmark run would delete a real practice trace, and its run would then
   * appear in the sandbox's run rail as though a player had made it. A benchmark trace is also
   * written at a different tick rate, so a review surface reading it would play it back against
   * the wrong clock.
   *
   * Isolation is by directory rather than by run-id namespacing because the run records index
   * (`runs.json`) and the eviction ordering are per-root: two writers into one root would still
   * evict each other's runs however their ids were chosen.
   */
  traceRoot?: string;
  /** Injected clock, so a test never depends on wall time. */
  now?: () => number;
  /**
   * **The run-lease facility** (`rounds-local-trace-store` "A run in progress is announced by a
   * lease"). Absent means the runtime has none, which is a real state and not a test convenience:
   * liveness is then `unknown`, and *nothing is reclaimed and nothing is reported*. See `run-lease.ts`
   * for why both of the available guesses are worse than declining.
   */
  locks?: LockManagerLike;
}

/** The measured default. ~229 MB a run against a ~10 GB origin quota fits ~45 runs, so five is
 * comfortably inside the medium and small enough that a player is never surprised by how much of
 * their device a practice sandbox holds (design "Risks": "the rule is needed but not tight"). */
export const DEFAULT_LOCAL_RETENTION = 5;

/**
 * **The read half of the store, and the whole of what the main thread gets.**
 *
 * ## STORE OWNERSHIP — the decision, and why (task 5.12; the next lane, 5.13, builds on it)
 *
 * {@link openLocalTraceStore} reclaims every leftover generation *before it serves any read*, which is
 * what makes an in-flight extent non-durable. OPFS **sync** access handles are worker-only, so the
 * writer — and therefore the generation — lives in the run's worker. The main thread nevertheless
 * needs `listRuns()` and `open(runId)` to draw the run rail and scrub published traces, **including
 * while a run is in flight**. A main-thread `openLocalTraceStore` would therefore reclaim the live
 * generation the worker is writing into: data loss as a side effect of opening a reader.
 *
 * The decision is **two named entry points, not a flag**:
 *
 *   * {@link openLocalTraceStore} is the **writer's** store. It reclaims, and it carries `beginRun`,
 *     `openInFlight` and `evictOldest`. Exactly one process opens it — the run's worker — and it opens
 *     it *before* `beginRun`, at the moment the spec names ("reclamation on the next initialisation of
 *     the store"), when no generation can be live: one run at a time, one worker per run.
 *   * {@link openLocalTraceReader} is everyone else's. It reclaims **nothing** and returns this
 *     narrowed shape, which has no `beginRun` and — the load-bearing part — **no `openInFlight`**.
 *
 * Why not a `reclaim: boolean` on one function: the honest default does not exist. `true` deletes a
 * live generation the day someone forgets to pass it; `false` leaves a store that can serve a
 * *previous session's* leftover extent, which is the same plausible-but-false-replay direction
 * **D-121** guards. `written-extent.ts` faced this and made its `site` a required field with no
 * default for exactly this reason. Two functions go one better: the wrong capability is not merely
 * un-defaulted, it is **absent from the type**.
 *
 * What that buys against the three scenarios this must keep honestly bound:
 *
 *   * `rounds-trace/a non-filesystem sink reclaims leftover generations on initialisation` — bound by
 *     the writer's store, which is the initialisation that precedes writing. Unchanged.
 *   * `rounds-local-trace-store/An unpublished trace is absent` — `open` resolves only against a
 *     published manifest in **both** shapes. Unchanged.
 *   * `rounds-trace/an in-flight extent is not durable and does not escape its writer` —
 *     **strengthened.** Durability is still reclamation-on-initialisation; escape is now structural:
 *     the only object with `openInFlight` is the one the writing worker constructed, so the extent
 *     cannot be reached from the main thread by any call, correct or mistaken. Before this, it was
 *     reachable and merely not reached.
 *
 * The consequence 5.13 must absorb: the main thread cannot read the in-flight extent at all, so
 * `SandboxWorld`'s in-flight arm has to be fed **from the worker** (which is where task 9.2's probe
 * put it, for the same reason), not by opening a second store beside it.
 */
export interface LocalTraceReadStore {
  /** The read surface's shapes, for enumeration. */
  readonly readShapes: readonly LocalReadShape[];
  /** Opens a locally written trace. Absent until its manifest is published. */
  open(runId: number): Promise<LocalTraceOpen>;
  /** The runs this device holds, newest first, evicted ones included. */
  listRuns(): Promise<readonly PracticeRunRecord[]>;
  /** The shipped seam the composition root injects into the practice screen. */
  asPracticeTraceStore(): PracticeTraceStore;
  /**
   * **Which runs have abandoned staged generations** (`rounds-local-trace-store` "Abandoned
   * generations are enumerable without exposing their bytes").
   *
   * Run identifiers, and nothing else. That distinction is the whole requirement: the ownership rule
   * exists because a staged generation has no manifest and no checksums, so its bytes past the write
   * head are unverified and must never reach a reader that believes it holds a published trace.
   * Learning that run *N* was abandoned reveals none of them — it is metadata *about* a directory,
   * never a view *into* one. There is still no `openInFlight` and no `beginRun` on this shape.
   *
   * Three kinds of leftover are deliberately **excluded**, each for its own reason:
   *
   *   * A generation whose lease is **held** — another tab is writing it right now.
   *   * A generation whose liveness is **unknown** (no lease facility) — "possibly live", so it is
   *     neither reclaimed nor reported.
   *   * A generation belonging to a run that **published**, whose staged directory a failed removal
   *     left behind. That run finished and is sitting in the player's run list; reporting it as
   *     interrupted would be manufacturing false evidence out of a tidy-up failure.
   */
  listAbandonedRuns(): Promise<readonly number[]>;
  /**
   * Removes an abandoned generation's bytes, **only if this process can take its lease**.
   *
   * Resolves `true` when the generation was reclaimed and `false` when the lease could not be taken
   * (another writer holds it) or the runtime offers no lease facility at all. Taking the lease *is*
   * the proof that no writer holds one, and it is held across the removal so none can be taken
   * mid-reclaim.
   *
   * This is on the read-only shape deliberately, and it is not a way in. It removes a directory; it
   * cannot begin a run and cannot read an in-flight extent, so the ownership rule the narrowed shape
   * exists to enforce is untouched. Without it the capability would be useless in the shape it exists
   * for: `client-practice` retires the interrupted-run notice **by reclaiming**, not by recording that
   * the player was told, so a store that could only enumerate would report the same interrupted run on
   * every boot for the life of the device (artifacts review **A1**).
   */
  reclaim(runId: number): Promise<boolean>;
}

export interface LocalTraceStore extends LocalTraceReadStore {
  /**
   * Reads a published component's bytes — whole, or a bounded byte range (`trace-colony-statistics`,
   * the local statistics reduction's read path).
   *
   * `range` omitted reads the whole component, which is what the reduction needs for `coarse`,
   * `index` and `balance` — small enough that a whole read costs nothing. `range` present reads only
   * `[offset, offset + length)`, via `File.slice` rather than `arrayBuffer()` on the whole file — the
   * one way `detail.bin` may be read here, since `rounds-local-run` requires it "traversed as bounded
   * ranges rather than materialising it whole".
   *
   * **On the writer's shape, not the narrowed reader's.** The reduction that drives this runs inside
   * the same worker that just published the trace (`practice-worker.ts`'s `reduceLocalStatistics`),
   * which is the one process already holding a {@link LocalTraceStore}; there is no scenario yet
   * where the main thread's {@link LocalTraceReadStore} needs it, and {@link LocalTraceReadStore}'s
   * own doc comment already treats its exact key set as a checked architectural boundary — widening it
   * for a capability nothing on that side calls would be exactly the kind of change that boundary
   * exists to make visible.
   *
   * **Published only, and unchecksummed.** This resolves straight against the published directory
   * with no manifest lookup and no checksum verification of its own — the reduction it serves runs
   * immediately after *this device's own* publish of the very trace it reads, so there is no network,
   * no other device and no other origin between the write and the read for a checksum to be guarding
   * against. `open`'s checksum-verified path is unaffected; this is a second, narrower entry point
   * beside it, exactly as `openInFlight` is.
   *
   * @throws whatever the medium throws for a missing run or component (a `NotFoundError`
   *   `DOMException`) — there is no absent-as-`null` arm, because every caller already wraps the whole
   *   reduction in a try/catch that degrades on any failure.
   */
  readPublishedComponent(
    runId: number,
    component: LocalComponentName,
    range?: { offset: number; length: number },
  ): Promise<Uint8Array>;
  /**
   * **The one in-process read of a staged generation** — the writer's own view of the bytes it has
   * already written, so the playhead chasing the write head has something to draw.
   *
   * `rounds-local-trace-store` grants exactly this and no more: "An in-flight trace MUST NOT be
   * served to any consumer outside the writing process, exported, or treated as durable; within that
   * constraint the review surface MAY read the portion already written **while the run is still
   * stepping**." Every clause of that constraint is structural here rather than promised:
   *
   *   * **Not served outside the writing process.** This method is on the store object, which only
   *     the composition root that owns the writer holds; it is deliberately absent from the narrowed
   *     {@link PracticeTraceStore} seam the screen is handed, so the screen's ordinary
   *     `open(runId)` cannot reach it. Nothing crosses a network, a route or a worker boundary.
   *   * **Not durable.** {@link openLocalTraceStore} reclaims every leftover generation *before it
   *     serves any read*, so a reload answers `absent` here for the same reason `open` does.
   *   * **Not exported.** The result is a decoded prefix, not a component: there is still no
   *     accessor anywhere that yields a component's bytes, and `writtenExtent` is still a count.
   *
   * ## Why this is a separate method and not a relaxation of {@link LocalTraceStore.open}
   *
   * `open`'s published-only resolution is the commit marker being enforced *by the reader*, and it is
   * enforced for every caller at once. Teaching it to fall back to `.generations/<runId>/` would put
   * the unverified path behind the same call the graded and published reads take — so a caller that
   * believed it had a checksum-verified trace could be holding one nobody checked, which is the
   * plausible-but-false-replay class **D-121** exists to prevent. `MemTraceSink` in
   * `waggle-round/src/trace/mem.rs` made exactly this judgement for exactly this reason ("deliberately
   * a *different* method, so no test can reach the in-flight extent by accident while believing it
   * read a published component"), and this follows it: two entry points, two result types, no
   * fallback between them.
   */
  openInFlight(runId: number): Promise<InFlightTraceOpen>;
  /** Begins a run's unpublished generation. */
  beginRun(runId: number): Promise<LocalGeneration>;
  /** Evicts the oldest scrubbable trace, returning its run id, or `null` when there is nothing left
   * to evict. Used both by retention and by the run driver's mid-run quota path. */
  evictOldest(): Promise<number | null>;
}

/**
 * A run's row as it is stored. Deliberately **without** a `scrubbable` flag.
 *
 * Whether a run can still be scrubbed is not an independent fact to be recorded — it is "are the
 * bytes still there", and storing it separately creates a state where the row and the medium can
 * disagree. That drift is reachable: eviction would be two operations (unlink the trace, update the
 * row) and a device that is out of room can fail the second, leaving a run listed as scrubbable with
 * nothing to scrub. So {@link LocalTraceStore.listRuns} *derives* it from the presence of a published
 * manifest, and eviction writes no row at all.
 */
type StoredRecord = PublishedRunFacts;

function isNotFound(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'NotFoundError';
}

/**
 * Whether a thrown value is the medium reporting it is out of room.
 *
 * Narrow on purpose: only `QuotaExceededError` triggers evict-and-retry. A permission failure or a
 * closed handle must never be mistaken for storage pressure, because retrying those would delete the
 * player's oldest run for no reason at all.
 *
 * It lives here rather than in the run driver that first needed it because storage exhaustion is a
 * fact about the **medium**, and the medium has two write sites, not one: the driver's per-tick
 * appends and this store's own publication copy (**D-216**). A second copy of the predicate beside
 * the second site is how the two would drift apart.
 */
export function isQuotaExhausted(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'QuotaExceededError') ||
    (error instanceof Error && error.name === 'QuotaExceededError')
  );
}

/**
 * Opens the store, **reclaiming every leftover unpublished generation first**.
 *
 * This is the browser's "startup reconciliation": `rounds-trace` says that "for a runtime with no
 * persistent process across a restart, 'startup reconciliation' means reclamation on the next
 * initialisation of the store". A tab closed mid-run ran no failure path at all, so the generation it
 * left behind is reclaimed here or never — and the reclamation happens **before any read is served**,
 * so an in-flight extent can never survive a reload into something a reader resolves against.
 */
export async function openLocalTraceStore(
  options: LocalTraceStoreOptions,
): Promise<LocalTraceStore> {
  return openStore(options, true);
}

/**
 * Opens the store **read-only, reclaiming nothing** — the main thread's view.
 *
 * See {@link LocalTraceReadStore} for the whole ownership decision this is one half of. In one
 * sentence: reclamation would delete the generation a run's worker is writing into at that moment, and
 * a non-reclaiming store must not be able to serve a *previous* session's leftover extent either — so
 * this shape has no `openInFlight` and no `beginRun` at all.
 */
export async function openLocalTraceReader(
  options: LocalTraceStoreOptions,
): Promise<LocalTraceReadStore> {
  const store = await openStore(options, false);
  // A fresh object rather than the full store: narrowing by type alone would leave the capabilities
  // reachable through a cast, and the point of the decision is that they are not there.
  //
  // `listAbandonedRuns` and `reclaim` are copied across deliberately and are not a widening of this
  // shape's contract. Neither reads a staged generation's bytes: one enumerates run identifiers, the
  // other removes a directory. What makes the narrowing load-bearing — no `beginRun`, and above all
  // no `openInFlight` — is untouched, so an unverified extent still cannot be reached from this side
  // by any call, correct or mistaken.
  return {
    readShapes: store.readShapes,
    open: store.open,
    listRuns: store.listRuns,
    asPracticeTraceStore: store.asPracticeTraceStore,
    listAbandonedRuns: store.listAbandonedRuns,
    reclaim: store.reclaim,
  };
}

async function openStore(
  options: LocalTraceStoreOptions,
  reclaimLeftoverGenerations: boolean,
): Promise<LocalTraceStore> {
  const retain = Math.max(1, options.retain ?? DEFAULT_LOCAL_RETENTION);
  const now = options.now ?? ((): number => Date.now());
  const root = await options.root.getDirectoryHandle(options.traceRoot ?? LOCAL_TRACE_ROOT, {
    create: true,
  });
  const generations = await root.getDirectoryHandle(GENERATIONS_DIR, { create: true });
  const locks = options.locks;

  /**
   * Reclaims one generation **iff this process can take its lease**.
   *
   * The lease is the gate for both callers — the writer store's open-time sweep below and the
   * read-only store's `reclaim` — because the hazard is identical either way: a check followed by an
   * unguarded delete races a writer that starts in between. Holding the lease across the removal
   * closes that window, and failing to take it is a definitive "someone else owns this".
   *
   * A `NotFoundError` is success, not failure: another process reclaiming the same directory first is
   * the outcome we wanted.
   */
  async function reclaimGeneration(runId: number): Promise<boolean> {
    return withRunLeaseIfFree(locks, runId, async () => {
      try {
        await generations.removeEntry(String(runId), { recursive: true });
      } catch (error) {
        if (!isNotFound(error)) throw error;
      }
    });
  }

  /**
   * **Reclamation, before anything is readable — and only of what no writer holds.**
   *
   * This used to delete *every* leftover generation unconditionally, which is the pre-existing hazard
   * `rounds-local-trace-store` names as fact (3): two tabs share one device credential and therefore
   * one store, so a tab opening the writer store to start a run would delete the generation another
   * tab's worker was writing into at that moment. The spec's "reclaimed on the next initialisation of
   * the store" accordingly becomes "reclaimed on the next initialisation **that can prove the writer
   * is gone**".
   *
   * What that does *not* weaken: an in-flight trace is still absent on restart in every case, because
   * `open` resolves only against a published manifest. Only the moment the bytes go away moves.
   *
   * Kept on the writer store as the **backstop** for a generation the main thread never saw — the
   * report path in `client-practice` reclaims on the practice restore, but a run whose tab died before
   * anything observed it has no such observer.
   */
  if (reclaimLeftoverGenerations) {
    for (const leftover of await directoryNames(generations)) {
      const runId = Number(leftover);
      if (!Number.isInteger(runId)) {
        continue;
      }
      await reclaimGeneration(runId);
    }
    // **And the published namespace, for the same reason.** Publication is a copy rather than a
    // rename (**D-216**), so it has a window in which `<runId>/` exists with some components in it
    // and no commit marker. `publish` reclaims that itself when it fails, but a tab that is killed
    // mid-copy runs no failure path at all — and a marker-less directory is reachable by *no* other
    // reclamation path: `evictOldest` iterates `runs.json` rows, and an uncommitted publication has
    // no row. Left alone it is storage the player can never get back.
    //
    // The predicate is `open`'s own — "a published directory whose manifest is missing is not a
    // trace either" — so nothing committed can be caught by it, and an evicted run (whose whole
    // directory went together) is not here to catch.
    for (const name of await directoryNames(root)) {
      if (name === GENERATIONS_DIR || name === RUN_RECORDS_FILE) continue;
      let published: OpfsDirectoryHandle;
      try {
        published = await root.getDirectoryHandle(name);
      } catch {
        // A file rather than a directory. Nothing in this namespace but `runs.json` should be one,
        // and it is skipped above — but a stray file is not a trace and is not this sweep's to judge.
        continue;
      }
      if ((await readManifestBytes(published)) === null) {
        await root.removeEntry(name, { recursive: true });
      }
    }
    // **And the published namespace, for the same reason.** Publication is a copy rather than a
    // rename (**D-216**), so it has a window in which `<runId>/` exists with some components in it
    // and no commit marker. `publish` reclaims that itself when it fails, but a tab that is killed
    // mid-copy runs no failure path at all — and a marker-less directory is reachable by *no* other
    // reclamation path: `evictOldest` iterates `runs.json` rows, and an uncommitted publication has
    // no row. Left alone it is storage the player can never get back.
    //
    // The predicate is `open`'s own — "a published directory whose manifest is missing is not a
    // trace either" — so nothing committed can be caught by it, and an evicted run (whose whole
    // directory went together) is not here to catch.
    for (const name of await directoryNames(root)) {
      if (name === GENERATIONS_DIR || name === RUN_RECORDS_FILE) continue;
      let published: OpfsDirectoryHandle;
      try {
        published = await root.getDirectoryHandle(name);
      } catch {
        // A file rather than a directory. Nothing in this namespace but `runs.json` should be one,
        // and it is skipped above — but a stray file is not a trace and is not this sweep's to judge.
        continue;
      }
      if ((await readManifestBytes(published)) === null) {
        await root.removeEntry(name, { recursive: true });
      }
    }
  }

  async function readRecords(): Promise<StoredRecord[]> {
    try {
      const handle = await root.getFileHandle(RUN_RECORDS_FILE);
      const text = await (await handle.getFile()).text();
      const parsed: unknown = JSON.parse(text);
      return Array.isArray(parsed) ? (parsed as StoredRecord[]) : [];
    } catch (error) {
      // A missing index is an empty store, not a failure. A *damaged* index is also treated as
      // empty rather than fatal: it holds no trace bytes, so the worst case is a run losing its
      // row — and refusing to open the store at all would cost the player every other run too.
      if (isNotFound(error) || error instanceof SyntaxError) return [];
      throw error;
    }
  }

  async function writeRecords(records: readonly StoredRecord[]): Promise<void> {
    const handle = await root.getFileHandle(RUN_RECORDS_FILE, { create: true });
    const access = await handle.createSyncAccessHandle();
    try {
      writeWhole(access, new TextEncoder().encode(JSON.stringify(records)));
    } finally {
      access.close();
    }
  }

  /** Newest first, which is the order the run rail reads. */
  function sortedNewestFirst(records: readonly StoredRecord[]): StoredRecord[] {
    return [...records].sort((a, b) => b.number - a.number);
  }

  /** Whether a run's bytes are still on the medium — the one source of truth for `scrubbable`. */
  async function isResident(runId: number): Promise<boolean> {
    try {
      const dir = await root.getDirectoryHandle(String(runId));
      return (await readManifestBytes(dir)) !== null;
    } catch (error) {
      if (isNotFound(error)) return false;
      throw error;
    }
  }

  async function residentRunIds(records: readonly StoredRecord[]): Promise<Set<number>> {
    const resident = new Set<number>();
    for (const record of records) {
      if (await isResident(record.runId)) resident.add(record.runId);
    }
    return resident;
  }

  async function evictOldest(): Promise<number | null> {
    const records = await readRecords();
    const resident = await residentRunIds(records);
    const victim = sortedNewestFirst(records)
      .filter((r) => resident.has(r.runId))
      .pop();
    if (victim === undefined) return null;
    // Detail, index, the coarse tier and the manifest that declared them go **together**. Keeping the
    // manifest would leave "a manifest claiming components that are gone", which the requirement
    // forbids by name; keeping the index without its detail is the same mistake in the other
    // direction.
    //
    // And **no row is written**. Two reasons, both load-bearing: the run's record is exactly what must
    // survive eviction, so eviction touching it is the one operation that could lose it; and eviction
    // must be reachable on a device that has *just refused a write for lack of room*, which a row
    // rewrite would not be.
    try {
      await root.removeEntry(String(victim.runId), { recursive: true });
    } catch (error) {
      if (!isNotFound(error)) throw error;
    }
    return victim.runId;
  }

  async function readComponent(
    dir: OpfsDirectoryHandle,
    component: LocalComponentName,
  ): Promise<Uint8Array | null> {
    try {
      const handle = await dir.getFileHandle(COMPONENT_FILES[component]);
      return new Uint8Array(await (await handle.getFile()).arrayBuffer());
    } catch (error) {
      if (isNotFound(error)) return null;
      throw error;
    }
  }

  /** {@link LocalTraceReadStore.readPublishedComponent}. Not routed through {@link readComponent}:
   * that helper's `null`-on-`NotFoundError` is for callers assembling a `LocalTraceOpen` verdict,
   * whereas this one's callers already treat any throw as "degrade the reduction". */
  async function readPublishedComponent(
    runId: number,
    component: LocalComponentName,
    range?: { offset: number; length: number },
  ): Promise<Uint8Array> {
    const dir = await root.getDirectoryHandle(String(runId));
    const handle = await dir.getFileHandle(COMPONENT_FILES[component]);
    const file = await handle.getFile();
    const blob = range === undefined ? file : file.slice(range.offset, range.offset + range.length);
    return new Uint8Array(await blob.arrayBuffer());
  }

  async function open(runId: number): Promise<LocalTraceOpen> {
    const records = await readRecords();
    const record = records.find((r) => r.runId === runId);

    let dir: OpfsDirectoryHandle;
    try {
      dir = await root.getDirectoryHandle(String(runId));
    } catch (error) {
      if (!isNotFound(error)) throw error;
      // No published directory. Which of the two "no bytes" answers this is depends entirely on
      // whether the run happened — and that is what the record is for.
      return record === undefined
        ? { kind: 'absent' }
        : { kind: 'evicted', record: toRecord(record, false) };
    }

    // The commit marker. An unpublished generation is a *different* directory and is never reached
    // from here, but a published directory whose manifest is missing is not a trace either.
    const manifestBytes = await readManifestBytes(dir);
    if (manifestBytes === null) {
      return record === undefined
        ? { kind: 'absent' }
        : { kind: 'evicted', record: toRecord(record, false) };
    }

    let manifest: TraceManifest;
    let rawManifest: Record<string, unknown>;
    try {
      rawManifest = JSON.parse(new TextDecoder().decode(manifestBytes)) as Record<string, unknown>;
      manifest = parseTraceManifest(rawManifest);
    } catch (error) {
      // Stale before damaged: an intact trace from an older build is not a fault, and reporting it
      // as one would make every routine format bump look like data loss on the player's device.
      // Nothing is removed here — see the `unsupported` arm's doc comment.
      if (error instanceof TraceUnsupportedVersionError) {
        return {
          kind: 'unsupported',
          formatVersion: error.formatVersion,
          supportedVersion: error.supportedVersion,
        };
      }
      return {
        kind: 'corrupt',
        component: 'detail',
        message: `the manifest could not be read: ${error instanceof Error ? error.message : String(error)}`,
      };
    }

    // The three decodable components come off the parsed manifest; the **balance** declaration comes
    // off the raw JSON, because `parseTraceManifest` deliberately drops it — the client does not
    // decode `balance.toml` (`net/rounds-trace.ts`: "A version-2 trace also declares `balance`, which
    // the client does not decode"). It must still be *checksummed*, though: it is the component a
    // re-simulation reconstructs its world from, so serving a trace whose balance does not match its
    // declaration is exactly the plausible-but-false replay **D-121** guards against. Not decoding a
    // component is not a reason to stop verifying it.
    const declared: Partial<Record<LocalComponentName, { checksum: string }>> = {
      coarse: manifest.components.coarse,
      detail: manifest.components.detail,
      index: manifest.components.index,
      balance: rawBalanceDeclaration(rawManifest),
    };
    const bytes: Partial<Record<LocalComponentName, Uint8Array>> = {};
    for (const component of ['coarse', 'detail', 'index', 'balance'] as const) {
      const decl = declared[component];
      if (decl === undefined) continue;
      const read = await readComponent(dir, component);
      if (read === null) {
        return {
          kind: 'corrupt',
          component: wireComponent(component),
          message: `${component} is declared by the manifest but absent from the trace`,
        };
      }
      // Checksums before structure. A component whose bytes are not the bytes that were published
      // must never be decoded, let alone served.
      if (!options.verifier.verify(decl.checksum, read)) {
        return {
          kind: 'corrupt',
          component: wireComponent(component),
          message: `${component} does not match the checksum its manifest declares`,
        };
      }
      bytes[component] = read;
    }

    let decoded: DecodedTrace;
    try {
      // `openTrace` is the shipped decoder, unchanged: it bounds-validates the index against the
      // actual detail length (every entry's `offset + length` inside the file, ticks strictly
      // increasing) and throws `TraceCorruptionError` when it does not fit. Reusing it rather than
      // reimplementing the validation is the point of holding the read surface identical.
      decoded = openTrace({
        manifest,
        coarse: bytes.coarse ?? new Uint8Array(0),
        detail: bytes.detail ?? new Uint8Array(0),
        index: bytes.index ?? new Uint8Array(0),
      });
    } catch (error) {
      if (error instanceof TraceCorruptionError) {
        return { kind: 'corrupt', component: error.component, message: error.message };
      }
      throw error;
    }

    return {
      kind: 'loaded',
      decoded,
      reader: readerOver(decoded),
      record: toRecord(
        record ?? {
          runId,
          number: 1,
          score: 0,
          seed: '',
          documentVersion: 0,
          completedAt: 0,
        },
        true,
      ),
    };
  }

  /**
   * The staged generation's readable prefix. See {@link LocalTraceStore.openInFlight} for why this
   * is a second entry point rather than a branch inside {@link open}.
   *
   * The read is of the *generation* directory — never the published one — and it reads the files the
   * writer has been appending to. Two things make that safe against the writer running concurrently.
   *
   * First, {@link decodeInFlightPrefix} truncates at the first record that is not wholly present, so
   * a read landing mid-append sees a shorter prefix, never a torn record.
   *
   * Second — and this is the part whose justification changed — the reader can see the bytes at all.
   * This comment used to argue that "the writer holds its sync access handle only for the duration of
   * one append", which **WAG-6 (`5ac9704b`) deliberately stopped being true**: `handleFor` now holds
   * each component's handle for the whole run and flushes only at `flushComponents()`. Correcting it
   * rather than leaving a safety invariant stated in terms of a mechanism that no longer exists, on
   * the one read path that races the writer. The property was separately re-established by
   * measurement: `getFile()` is not blocked by an open sync access handle and does observe unflushed
   * writes — `docs/dev/evidence/wag6-opfs-held-handle/`. So the guarantee now rests on that measured
   * behaviour plus the truncating decode, not on handle lifetime.
   */
  async function openInFlight(runId: number): Promise<InFlightTraceOpen> {
    let dir: OpfsDirectoryHandle;
    try {
      dir = await generations.getDirectoryHandle(String(runId));
    } catch (error) {
      if (!isNotFound(error)) throw error;
      // No generation: never begun, discarded, published, or reclaimed by a reload. All four are
      // honestly the same answer here — there is nothing being written.
      return { kind: 'absent' };
    }
    const empty = new Uint8Array(0);
    return {
      kind: 'in-flight',
      prefix: decodeInFlightPrefix({
        coarse: (await readComponent(dir, 'coarse')) ?? empty,
        detail: (await readComponent(dir, 'detail')) ?? empty,
        index: (await readComponent(dir, 'index')) ?? empty,
      }),
    };
  }

  async function beginRun(runId: number): Promise<LocalGeneration> {
    // A leftover generation for the same run is unpublished rubbish by definition, exactly as
    // `FsTraceSink::create` treats it.
    try {
      await generations.removeEntry(String(runId), { recursive: true });
    } catch (error) {
      if (!isNotFound(error)) throw error;
    }
    const dir = await generations.getDirectoryHandle(String(runId), { create: true });
    const extents: Record<LocalComponentName, number> = {
      coarse: 0,
      detail: 0,
      index: 0,
      balance: 0,
    };
    let detailRecords = 0;
    let published = false;

    // The held sync access handles (WAG-6) — one per component that has actually been appended
    // to, acquired lazily. A component that never appends never gets an entry here, and therefore
    // never gets a file on the medium either: `handleFor` is the only thing that calls
    // `getFileHandle({ create: true })` for a component file, and it does so only on that
    // component's first append. That laziness is load-bearing, not incidental — `readComponent`
    // and `publish` below both treat "no file" as "this component was never written", and an
    // eagerly created empty file would turn that into a false "written but empty".
    const handles: Partial<Record<LocalComponentName, OpfsSyncAccessHandle>> = {};

    /** Acquires a component's sync access handle once and reuses it for every later append and
     * flush. Acquisition is the only async part of a write — `FileSystemSyncAccessHandle.write`
     * is itself synchronous — so paying the acquisition cost once per component per run, rather
     * than once per append, is the whole fix for WAG-6's throughput regression (measured: ~9 ms
     * per acquisition, 2-3 appends per tick over ~27 000 ticks). */
    async function handleFor(component: LocalComponentName): Promise<OpfsSyncAccessHandle> {
      const existing = handles[component];
      if (existing !== undefined) return existing;
      const handle = await dir.getFileHandle(COMPONENT_FILES[component], { create: true });
      const access = await handle.createSyncAccessHandle();
      handles[component] = access;
      return access;
    }

    /** Closes every held handle and clears the cache. Idempotent — a generation may have
     * `discard` called on it after it has already published (see `discard` below), so this must
     * tolerate running with nothing left to close. Both `publish` and `discard` call this before
     * they remove the generation directory: a directory containing a file with an open sync
     * access handle cannot be removed. */
    function closeHandles(): void {
      for (const component of ['coarse', 'detail', 'index', 'balance'] as const) {
        const access = handles[component];
        if (access === undefined) continue;
        access.close();
        delete handles[component];
      }
    }

    async function appendTo(component: LocalComponentName, data: Uint8Array): Promise<void> {
      const access = await handleFor(component);
      // A failed write commits nothing, so the extent is only advanced once the write returns.
      // That is what makes "no partially written trace readable" true of the counter as well as
      // of the bytes.
      access.write(data, { at: extents[component] });
      extents[component] += data.byteLength;
    }

    return {
      async append(component, data): Promise<void> {
        if (published) throw new Error('the generation is already published');
        await appendTo(component, data);
        if (component === 'detail') detailRecords += 1;
      },
      async flushComponents(): Promise<void> {
        // Flushes the handles `append` already holds open — it must not reacquire, both because
        // reacquiring is exactly the per-append cost WAG-6 removes and because a second sync
        // access handle on a file whose first is still open is refused by the medium.
        // `extents[component] === 0` is still the right test for "was this ever appended to": it
        // is true exactly when `handleFor` was never called for it, so nothing here creates a
        // file for a component that received no bytes.
        for (const component of ['coarse', 'detail', 'index', 'balance'] as const) {
          if (extents[component] === 0) continue;
          const access = await handleFor(component);
          access.flush();
        }
      },
      async writeManifest(data): Promise<void> {
        // Inside the still-unpublished generation, and last. Nothing may be appended after this —
        // the Rust sink's ordering guarantees it, and the `published` guard above enforces it here.
        const handle = await dir.getFileHandle(MANIFEST_FILE, { create: true });
        const access = await handle.createSyncAccessHandle();
        try {
          writeWhole(access, data);
        } finally {
          access.close();
        }
      },
      async publish(facts): Promise<LocalPublication> {
        const manifestBytes = await readManifestBytes(dir);
        if (manifestBytes === null) {
          // Structural, not a policy check: publishing without a commit marker would create the one
          // thing the format forbids — a readable trace with no manifest.
          throw new Error('the manifest is the commit marker and must be written before publication');
        }
        // Every handle `append`/`flushComponents` held open closes here, before this generation
        // directory is removed a few lines down (`generations.removeEntry`) — a directory
        // containing a file with an open sync access handle cannot be removed.
        closeHandles();
        // Retention first, so the store is never transiently over its ceiling.
        let held = (await residentRunIds(await readRecords())).size;
        while (held >= retain) {
          if ((await evictOldest()) === null) break;
          held -= 1;
        }
        // The atomic publication: the generation becomes the published trace. OPFS has no rename, so
        // the move is a copy of the already-flushed bytes followed by the generation's removal — and
        // crucially the **manifest is copied last**, so an interruption part-way leaves a directory
        // with no commit marker, which every reader treats as absent.
        //
        // **This copy is the second place the medium can run out of room** (**D-216**): it roughly
        // doubles the run's peak footprint, and it happens after every tick's append has already
        // succeeded. So `rounds-local-run`'s "on a write failure it SHALL evict the oldest retained
        // trace and retry the write **once**" has to hold here too — the run driver's
        // `writeWithQuotaRecovery` wraps `append` and cannot reach inside this.
        //
        // Everything the publication writes is inside `commit`, including the run's row, so the whole
        // of it either lands or is reclaimed. A committed trace with no row would be the same leak in
        // a quieter form: `open` would serve it, and `evictOldest` — which iterates `runs.json` —
        // could never reclaim it.
        const commit = async (): Promise<void> => {
          const target = await root.getDirectoryHandle(String(facts.runId), { create: true });
          for (const component of ['coarse', 'detail', 'index', 'balance'] as const) {
            const bytes = await readComponent(dir, component);
            if (bytes === null) continue;
            const handle = await target.getFileHandle(COMPONENT_FILES[component], { create: true });
            const access = await handle.createSyncAccessHandle();
            try {
              writeWhole(access, bytes);
            } finally {
              access.close();
            }
          }
          const marker = await target.getFileHandle(MANIFEST_FILE, { create: true });
          const markerAccess = await marker.createSyncAccessHandle();
          try {
            writeWhole(markerAccess, manifestBytes);
          } finally {
            markerAccess.close();
          }
          await writeRecords([
            ...(await readRecords()).filter((r) => r.runId !== facts.runId),
            { ...facts, completedAt: facts.completedAt || now() },
          ]);
        };

        /** Removes whatever the failed attempt left in the published namespace.
         *
         * Unconditional, not only on the quota path: **any** failure part-way leaves a directory the
         * caller cannot see and no reclamation path can reach. It also removes a *previous* trace
         * published under the same run id — which is right rather than destructive, because the copy
         * has already overwritten some of its components and its surviving manifest would declare
         * checksums for bytes that are no longer there. `evicted` is the honest outcome for that;
         * `corrupt` is not. */
        const reclaimPublished = async (): Promise<void> => {
          try {
            await root.removeEntry(String(facts.runId), { recursive: true });
          } catch (error) {
            if (!isNotFound(error)) throw error;
          }
        };

        try {
          await commit();
        } catch (error) {
          // Reclaim first: the partial copy's bytes are the ones standing between the retry and the
          // room it needs, and freeing them costs nothing when the failure was not about room at all.
          await reclaimPublished();
          // Exactly once. A loop would turn a full device into an eviction cascade that silently
          // deletes every run the player has, and `evictOldest` answering `null` means there was
          // nothing left to evict, in which case the retry cannot help and is not attempted.
          if (!isQuotaExhausted(error) || (await evictOldest()) === null) throw error;
          try {
            await commit();
          } catch (retryError) {
            await reclaimPublished();
            throw retryError;
          }
        }
        published = true;
        // **The staged copy's removal is a warning, never a publication failure**
        // (`rounds-local-trace-store` "A failure to remove a published run's staged directory is not
        // swallowed"; architecture review **F3(b)**).
        //
        // This was `.catch(() => {})`. Swallowing it does not merely leak disk — it **manufactures
        // false evidence**: a successfully published run whose cleanup failed leaves a staged
        // directory byte-for-byte indistinguishable from an interrupted run's, so the player would
        // later be told a run did not finish when it finished and is sitting in their run list.
        //
        // Reported rather than thrown, following `waggle_round::trace::writer`'s
        // `Publication::durability_warning` exactly: the manifest is the commit marker, so the run is
        // published whether or not the staged copy could be tidied away afterwards. `listAbandonedRuns`
        // independently excludes any run with a published record, so the false-evidence path is closed
        // even where nobody reads this warning.
        try {
          await generations.removeEntry(String(facts.runId), { recursive: true });
          return { stagingRemovalWarning: null };
        } catch (error) {
          if (isNotFound(error)) {
            // Already gone. That is the outcome we wanted, not a failure to report.
            return { stagingRemovalWarning: null };
          }
          return {
            stagingRemovalWarning:
              error instanceof Error ? error.message : String(error),
          };
        }
      },
      async discard(): Promise<void> {
        if (published) return;
        // Same reason as `publish`: every held handle closes before the directory holding its
        // file is removed below.
        closeHandles();
        try {
          await generations.removeEntry(String(runId), { recursive: true });
        } catch (error) {
          if (!isNotFound(error)) throw error;
        }
        for (const component of ['coarse', 'detail', 'index', 'balance'] as const) {
          extents[component] = 0;
        }
        detailRecords = 0;
      },
      writtenExtent(component): number {
        return extents[component];
      },
      get inFlightTicks(): number {
        return detailRecords;
      },
    };
  }

  async function listRuns(): Promise<readonly PracticeRunRecord[]> {
    const records = await readRecords();
    const resident = await residentRunIds(records);
    return sortedNewestFirst(records).map((r) => toRecord(r, resident.has(r.runId)));
  }

  /**
   * Reports whether a staged generation is being written, **acquiring nothing and reading no bytes**.
   *
   * Exposed on the store rather than left to callers so the answer for a run with no staged directory
   * at all is stated once: `abandoned` would be wrong (there is nothing to reclaim) and `live` would be
   * wrong (nobody is writing), so a directoryless run is simply not enumerable below and this reports
   * on the lease alone.
   */
  async function runLiveness(runId: number): Promise<RunLiveness> {
    return probeRunLiveness(locks, runId);
  }

  async function listAbandonedRuns(): Promise<readonly number[]> {
    const names = await directoryNames(generations);
    if (names.length === 0) {
      return [];
    }
    // A run with a published record is **not** abandoned however its staged directory came to be left
    // behind. `publish` surfaces a removal failure now instead of swallowing it, but the leftover
    // directory still exists on the medium afterwards, and it is byte-for-byte indistinguishable from
    // an interrupted run's. The records index is what tells them apart, and it is the commit marker's
    // own testimony — so this is not a heuristic.
    const publishedIds = new Set((await readRecords()).map((record) => record.runId));
    const abandoned: number[] = [];
    for (const name of names) {
      const runId = Number(name);
      if (!Number.isInteger(runId) || publishedIds.has(runId)) {
        continue;
      }
      // `live` means another tab is writing it; `unknown` means we cannot tell and must treat it as
      // possibly live. Only a definite `abandoned` is reportable.
      if ((await runLiveness(runId)) === 'abandoned') {
        abandoned.push(runId);
      }
    }
    return abandoned.sort((a, b) => a - b);
  }

  return {
    readShapes: LOCAL_READ_SHAPES,
    open,
    openInFlight,
    listRuns,
    beginRun,
    evictOldest,
    listAbandonedRuns,
    reclaim: reclaimGeneration,
    readPublishedComponent,
    asPracticeTraceStore(): PracticeTraceStore {
      return {
        async open(runId: number): Promise<TraceLoad> {
          const result = await open(runId);
          switch (result.kind) {
            case 'loaded':
              // The shipped decoder's own output, handed to the shipped review surface unchanged.
              // `tickRateHz` has no on-disk field to read here (the trace's manifest carries no
              // rate — E5 is what puts one on the SERVED envelope, which this local read path has
              // no equivalent of). **fallback-permitted** (client-tick-rate-provenance,
              // tick-rate-single-source task 5.4): a device-executed practice run's engine is
              // `waggle-wasm`, compiled from the same `Balance::seed()` the generated mirror is
              // generated from, so the mirror provably IS the running rate — and, from WAG-100,
              // provably IS the per-cell store capacity the run's hive filled up against.
              return {
                kind: 'loaded',
                trace: reviewTraceFromDecoded(result.decoded, {
                  tickRateHz: SIM_TICK_RATE_HZ,
                  perCellCapacity: HIVE_PER_CELL_CAPACITY,
                }),
              };
            case 'corrupt':
              return { kind: 'corrupt', component: result.component, message: result.message };
            case 'unsupported':
              // Carried through as itself. `TraceLoad` grew this arm rather than the store folding
              // it into `corrupt`, because the whole point of the distinction is that it survives
              // to the surface that presents it.
              return {
                kind: 'unsupported',
                formatVersion: result.formatVersion,
                supportedVersion: result.supportedVersion,
              };
            case 'evicted':
              // Scrubbing is **unavailable rather than erroring**. `TraceLoad` cannot say that, so
              // the narrowing says the truest thing it can and the run rail — which reads
              // `listRuns()`, where the row is marked unscrubbable — is where a player learns why.
              return {
                kind: 'corrupt',
                component: 'detail',
                message: 'This run’s replay was cleared to make room. Its score is still recorded.',
              };
            case 'absent':
              return {
                kind: 'corrupt',
                component: 'detail',
                message: 'This device holds no replay for that run.',
              };
          }
        },
        listRuns,
        // Both lease-gated, and neither a way into a staged generation's bytes: one yields run
        // identifiers, the other removes a directory. They are on the screen-facing seam because the
        // interrupted-run report is made and retired from the practice restore path, which holds this
        // shape and not the store.
        listAbandonedRuns,
        reclaim: reclaimGeneration,
      };
    },
  };
}

/**
 * The `balance` component's declaration, read off the raw manifest JSON.
 *
 * `undefined` when the manifest declares no balance component at all — a format-version-1 trace,
 * which no local run can produce (the local writer is the current writer) but which a reader should
 * not crash on. A declaration that is *present but malformed* returns an empty checksum, which every
 * verifier rejects: an unreadable declaration is not a satisfied one.
 */
function rawBalanceDeclaration(raw: Record<string, unknown>): { checksum: string } | undefined {
  const components = raw.components;
  if (components === null || typeof components !== 'object') return undefined;
  const balance = (components as Record<string, unknown>).balance;
  if (balance === null || typeof balance !== 'object') return undefined;
  const checksum = (balance as Record<string, unknown>).checksum;
  return { checksum: typeof checksum === 'string' ? checksum : '' };
}

async function readManifestBytes(dir: OpfsDirectoryHandle): Promise<Uint8Array | null> {
  try {
    const handle = await dir.getFileHandle(MANIFEST_FILE);
    return new Uint8Array(await (await handle.getFile()).arrayBuffer());
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

async function directoryNames(dir: OpfsDirectoryHandle): Promise<string[]> {
  const names: string[] = [];
  for await (const name of dir.keys()) names.push(name);
  return names;
}

/** The `balance` component has no wire name (the client does not decode it), so a corruption in it
 * is reported against `detail` — the component whose reconstruction it would have governed. */
function wireComponent(component: LocalComponentName): TraceComponentName {
  return component === 'balance' ? 'detail' : component;
}

/** A stored row plus the derived residency answer. `scrubbable` is never read from storage — see
 * {@link StoredRecord}. */
function toRecord(stored: StoredRecord, scrubbable: boolean): PracticeRunRecord {
  return {
    runId: stored.runId,
    number: stored.number,
    score: stored.score,
    // **Read defensively, because the index is a file this build did not necessarily write.** A row
    // stored before these two fields existed carries neither, and the honest reading of that is
    // "unattributed" / "unknown" rather than ordinal `0` and the empty seed — both of which are
    // values a caller would render as fact. Same reasoning as `readRecords`' damaged-index arm.
    documentVersion:
      typeof stored.documentVersion === 'number' && stored.documentVersion > 0
        ? stored.documentVersion
        : null,
    seed: typeof stored.seed === 'string' && stored.seed !== '' ? stored.seed : null,
    completedAt: stored.completedAt,
    scrubbable,
  };
}

/** Wraps a decoded trace in **exactly** the two read shapes. Built with an object literal whose own
 * keys are the two shapes, so `Object.keys` on it is the surface's enumeration. */
function readerOver(decoded: DecodedTrace): LocalTraceReader {
  return {
    coarseWhole(): readonly CoarseSample[] {
      return decoded.coarseSamples;
    },
    detailByRange(from: number, to: number): LocalDetailRange {
      if (to < from) {
        throw new LocalRangeError(
          'inverted_range',
          `detail range ${from}..${to} is inverted; it is refused rather than reordered`,
        );
      }
      if (to - from + 1 > LOCAL_MAX_DETAIL_TICKS) {
        throw new LocalRangeError(
          'range_too_wide',
          `detail range ${from}..${to} spans ${to - from + 1} ticks, over the reader's ${LOCAL_MAX_DETAIL_TICKS}-tick maximum; it is refused rather than clamped`,
        );
      }
      const records: DetailRecord[] = [];
      for (let tick = from; tick <= to; tick += 1) {
        const record = decoded.detailAt(tick);
        if (record !== null) records.push(record);
      }
      // A range the detail tier does not cover is **absent** — a normal result, deliberately not the
      // typed refusal an inverted or over-wide range gets.
      return records.length === 0 ? { kind: 'absent' } : { kind: 'records', records };
    },
  };
}
